import { useEffect } from 'react';

import { isNotNullOrUndefined } from 'Util/TypeGuards';
import {
	findAllMeasurementsForFerryTrip,
	getMeasurementValueFromId,
	resolveAllowedMeasurementId,
} from 'Util/_HumanWritten/MeasurementUtils';
import { BookingWizardData } from '../../../BookingWizardData/BaseBookingWizardData';
import {
	isVehicleBookingWizardData,
	VehicleBookingWizardData,
} from '../../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../../BookingWizardDataStore';
import { useVehicleTabContext } from '../VehicleTabContext';
import { useMeasurementFilterContext } from './useMeasurementFilterContext';

/**
 * Moves the selected length off a measurement the operator's rules no longer allow.
 *
 * The case this exists for: a customer picks a length, then changes the driver from an adult to a
 * senior. Leaving the old length selected is what the feature is meant to stop — that booking is on
 * the wrong fare and someone refunds the difference later. So the selection is replaced with the
 * smallest length that is both allowed and still large enough for the vehicle, and the price
 * recalculates the same way it does for any other length change.
 *
 * The decision itself is `resolveAllowedMeasurementId`, shared with check-in so the two flows cannot
 * drift. It prefers the band of the same size for the new driver. Two things it will not do: never
 * clear a selection, and never go below the floors already enforced elsewhere.
 *
 * When altering an existing booking, the floor includes the length currently selected, as check-in
 * Edit does. Removing the adult so the senior drives must turn "Up to 25'" into the senior
 * "Up to 25'", and when no senior band is that long, leave the length alone rather than shrink the
 * vehicle on the booking (#2830).
 */
export function useResetDisallowedMeasurements() {
	const { cargoTypes, towOnTypes } = useVehicleTabContext();
	const { wizardData, setWizardData, oldWizardData } = useBookingWizardDataStore();
	const filterContext = useMeasurementFilterContext();

	const inner = wizardData?.innerWizardData as VehicleBookingWizardData | undefined;
	const cargoTypeId = inner?.cargoTypeId;
	const vehicleLengthId = inner?.vehicleLengthId;
	const trailerLengthId = inner?.trailerLengthId;
	const trailerTypeId = inner?.trailerTypeId;

	useEffect(() => {
		if (!isVehicleBookingWizardData(wizardData?.innerWizardData)) {
			return;
		}
		const data = wizardData.innerWizardData;

		const oldInner = isNotNullOrUndefined(oldWizardData)
			? (oldWizardData.innerWizardData as VehicleBookingWizardData)
			: undefined;
		const restrictedFloor = (id?: string) => (wizardData.restrictedMode && id ? getMeasurementValueFromId(id) : 0);
		const alterationFloor = (id?: string) =>
			wizardData.wizardMode === 'ALTERATION' && id ? getMeasurementValueFromId(id) : 0;

		let next: BookingWizardData | undefined;

		// Built from the store's current state, not from this render's `wizardData`. The effect
		// writes the whole wizard object, so anything another effect committed since this render —
		// child effects run before parent ones, and several of them write wizardData — would be
		// reverted by a copy taken at render time.
		const apply = (mutate: (target: VehicleBookingWizardData) => void) => {
			next = next ?? new BookingWizardData({ ...useBookingWizardDataStore.getState().wizardData });
			if (isVehicleBookingWizardData(next.innerWizardData)) {
				mutate(next.innerWizardData);
			}
		};

		const cargoType = cargoTypes.find(x => x.id === data.cargoTypeId);
		const vehicleReplacement = resolveAllowedMeasurementId(
			vehicleLengthId,
			findAllMeasurementsForFerryTrip('LENGTH', 'vehicle'),
			filterContext.vehicle,
			Math.max(
				getMeasurementValueFromId(cargoType?.minimumLengthId),
				restrictedFloor(oldInner?.vehicleLengthId),
				alterationFloor(vehicleLengthId),
			),
		);
		if (vehicleReplacement) {
			apply(target => {
				target.vehicleLengthId = vehicleReplacement;
			});
		}

		if (trailerTypeId && trailerTypeId !== 'NO_TRAILER') {
			const towOnType = towOnTypes.find(x => x.id === trailerTypeId);
			const trailerReplacement = resolveAllowedMeasurementId(
				trailerLengthId,
				findAllMeasurementsForFerryTrip('LENGTH', 'trailer'),
				filterContext.trailer,
				Math.max(
					getMeasurementValueFromId(towOnType?.minimumLengthId),
					restrictedFloor(oldInner?.trailerLengthId),
					alterationFloor(trailerLengthId),
				),
			);
			if (trailerReplacement) {
				apply(target => {
					target.trailerLengthId = trailerReplacement;
				});
			}
		}

		if (next) {
			setWizardData(next);
		}
		// Keyed on the resolved context rather than on wizardData as a whole: this effect writes
		// wizardData, so depending on all of it would re-run on its own write.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		filterContext.vehicle.driverPassengerTypeId,
		filterContext.vehicle.userDisplayNameId,
		filterContext.vehicle.cargoCharacteristicIds,
		filterContext.trailer.cargoCharacteristicIds,
		vehicleLengthId,
		trailerLengthId,
		cargoTypeId,
		trailerTypeId,
		cargoTypes,
		towOnTypes,
	]);
}
